import { useEffect, useRef, useState } from "react";
import { RenderSeaSearchErrorCardProps } from "entities/shared/seaCard.types";
import { appStore } from "entities/App";
import { authStore } from "entities/Auth";
import { ErrorCard, LoadCard } from "entities/Card";
import { SealineSelect } from "entities/Sealines";
import { createSeaSearchQueryParams, seaSearchCardStore, useSearchQuery } from "entities/SeaSearchCard";
import { subscriptionStore } from "entities/Subscription";
import { searchShipmentLoadingText } from "shared/constants/panel.constants";
import { useAppDispatch, useAppSelector } from "shared/lib/redux";
import { useTranslation } from "shared/localization/localization";
import styles from "./SearchErrorCard.module.scss";

export const SeaSearchErrorCard: RFC<RenderSeaSearchErrorCardProps> = ({
  status,
  reference,
  text,
  onRetry,
  onDelete,
  className,
  canChangeShippingLine,
  referenceType,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const isAuthenticated = useAppSelector(authStore.selectors.isAuthenticated);
  const [selectedScacCode, setSelectedScacCode] = useState("");
  const uniqueId = useRef<string>(Date.now().toString());

  const { searchRequest, isSearchLoading } = useSearchQuery({
    queryKey: [reference ?? uniqueId.current],
    isAuthenticated,
    setSubscription: (subscription) => ({
      type: subscriptionStore.actions.setSubscription.type,
      payload: subscription,
    }),
    setActivePaymentModal: (activePaymentModal) => ({
      type: subscriptionStore.actions.setActivePaymentModal.type,
      payload: activePaymentModal,
    }),
    setAppIntegrationRequestLimitReached: (appIntegrationRequestLimitReached) => ({
      type: appStore.actions.setAppIntegrationRequestLimitReached.type,
      payload: appIntegrationRequestLimitReached,
    }),
    setWebAppRequestLimitReached: (webAppRequestLimitReached) => ({
      type: appStore.actions.setWebAppRequestLimitReached.type,
      payload: webAppRequestLimitReached,
    }),
  });

  useEffect(() => {
    if (selectedScacCode) {
      const reqParams = createSeaSearchQueryParams({
        model: {
          reference: reference,
          referenceType: referenceType,
          carrierScac: selectedScacCode,
        },
        requestType: "shippingLine",
        override: { carrier_code: selectedScacCode },
      });

      if (!reqParams) return;

      searchRequest(reqParams, {
        onSuccess: (response) => {
          dispatch(seaSearchCardStore.actions.updateSearchCardAction(response));
        },
      });
    }
  }, [selectedScacCode]);

  if (isSearchLoading) {
    return <LoadCard text={t(searchShipmentLoadingText)} />;
  }

  return (
    <ErrorCard
      text={text}
      status={canChangeShippingLine ? "not-defined" : status}
      reference={reference}
      onDelete={onDelete}
      onRetry={onRetry}
      className={className}
    >
      {canChangeShippingLine && (
        <SealineSelect
          value={selectedScacCode}
          onChange={(value) => setSelectedScacCode(value.value)}
          className={styles.select}
          isDropDownMenuRelative={false}
        />
      )}
    </ErrorCard>
  );
};
